import React, { useContext, useEffect } from 'react';
import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation, withTranslation } from 'next-i18next';
import {
    ContentSection,
    PrivacySectionImage,
    RespectingYourPrivacySection,
    SectionContent,
    SectionHeading,
} from 'pages/splash/styles';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { defaultLocale } from '~/locale/defaults';
import PageTitle from '~/components/common/page-title';
import BaseLayout from '~/components/common/layout';
import { urlWithBasePath } from '~/utils/url-helpers';
import { TextParagraph } from '~/theme/elements';
import getLocale from '~/locale/get-locale';
import Spinner from '~/components/common/spinner';
import sanitizeHtml from '~/utils/sanitize-html';
import ServicesAvailable from '~/components/splash/services-available';
import { storeContext } from '~/store/store';
import { SET_INTENDED_FLOW, SET_RENEWAL_FLOW } from '~/store/actions';

function Splash() {
    const { t } = useTranslation();
    const { status } = useSession();
    const router = useRouter();
    const { dispatch } = useContext(storeContext);
    const { redirectUri, flow, loggedOut } = router.query as {
        redirectUri?: string;
        flow?: string;
        loggedOut?: string;
    };
    const isLogoutLanding =
        loggedOut === '1' ||
        (typeof window !== 'undefined' && sessionStorage.getItem('postLogoutLanding') === 'true');

    useEffect(() => {
        if (redirectUri) {
            if (redirectUri.startsWith('http') || !redirectUri.startsWith('/')) {
                router.replace('/404');
                return;
            }
            sessionStorage.setItem('redirectUri', redirectUri as string);
        }

        dispatch({
            type: SET_INTENDED_FLOW,
            payload: { intendedFlow: flow ? flow.toUpperCase() : '' },
        });

        dispatch({
            type: SET_RENEWAL_FLOW,
            payload: {
                renewalFlow: '',
            },
        });
    }, [flow, dispatch]);

    useEffect(() => {
        if (status === 'authenticated' && !isLogoutLanding) {
            router.push('/verify-user');
            return;
        }

        if (status === 'unauthenticated' && typeof window !== 'undefined') {
            sessionStorage.removeItem('postLogoutLanding');
        }

        const locale = getLocale();
        if (locale && locale !== router.locale) {
            const { pathname, asPath, query } = router;
            router.push({ pathname, query }, asPath, { locale });
        }
    }, [router, status, isLogoutLanding]);

    if ((status === 'authenticated' && !isLogoutLanding) || !sanitizeHtml) {
        return <Spinner isLoading />;
    }

    return (
        <>
            <PageTitle title={t('splash:title')} />
            <BaseLayout showSplashBanner>
                <ContentSection>
                    <ServicesAvailable />

                    <RespectingYourPrivacySection>
                        <PrivacySectionImage
                            src={urlWithBasePath('/images/respecting-your-privacy.png')}
                            height={298}
                            width={515}
                            alt={t('splash:RespectingYourPrivacy.heading') ?? ''}
                        />
                        <SectionContent>
                            <SectionHeading>
                                {t('splash:RespectingYourPrivacy.heading')}
                            </SectionHeading>
                            <TextParagraph
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(
                                        t('splash:RespectingYourPrivacy.leadText', {
                                            privacyURL: t('common:serviceOntarioPrivacyPage'),
                                        }) ?? '',
                                        {
                                            FORCE_BODY: true,
                                            ALLOWED_TAGS: ['a', 'br'],
                                            ALLOWED_ATTR: ['href', 'target'],
                                        }
                                    ),
                                }}
                            />
                        </SectionContent>
                    </RespectingYourPrivacySection>
                </ContentSection>
            </BaseLayout>
        </>
    );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
    props: {
        ...(await serverSideTranslations(locale ?? defaultLocale, [
            'splash',
            'dashboard',
            'base-layout',
            'common',
        ])),
    },
});

export default withTranslation(['splash', 'dashboard', 'base-layout', 'common'])(Splash);
